import clsx, { ClassValue } from 'clsx'
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { isValueOfObject } from '@afterpay/utils/object'
import { Divider, Tabs, useIsBreakpointMediumAndUp, useTabDeepLink } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { useSearchParam } from '../../hooks/useSearchParam'
import { PaymentMethods } from './PaymentMethods'
import { ProfileTab } from './Profile'
import { PreferencesTab } from './Preferences'
import { PulseTab } from './Pulse'
import { SecurityTab } from './Security'
import styles from './style.module.css'
import { useIsAlignPaydayEligible, useIsPulseEnabled } from '../../state'
import { useFlag } from '../../hooks'
import { PreferredDayRemoval } from './PaymentMethods/PreferredDayRemoval'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string | number
  selectedValue: string | number
}

export interface AccountTabsType {
  Profile: 'profile'
  PaymentMethods: 'payment-methods' | 'payment-settings'
  Preferences?: 'preferences'
  Security: 'security'
  Pulse?: 'pulse'
}

type AccountTabType = 'profile' | 'pulse' | 'payment-methods' | 'payment-settings' | 'preferences' | 'security'

const TabPanel: FunctionComponent<TabPanelProps> = ({ value, selectedValue, children }: TabPanelProps) => {
  const isBpMediumUp: boolean = useIsBreakpointMediumAndUp() as boolean
  const classNames: ClassValue = clsx(styles.tabPanel, isBpMediumUp && styles.bpMediumUp)
  return (
    <div role='tabpanel' className={classNames} hidden={selectedValue !== value} id={`tabpanel-${value}`} aria-labelledby={`tabpanel-${value}`}>
      {value === selectedValue && <>{children}</>}
    </div>
  )
}

const I18N_NAMESPACE = ['common', 'account', 'loyalty']

const useTabRefs = (object: AccountTabsType): { [key: string]: React.RefObject<any> } => {
  const tabRefs = useMemo(() => ({}), [])

  useEffect(() => {
    Object.values(object).forEach((val) => (tabRefs[val] = React.createRef()))
  }, [object, tabRefs])

  return tabRefs
}

const useScrollIntoView = (targetRefMap: { [key: string]: React.RefObject<any> }, target: string, options?: boolean | ScrollIntoViewOptions): void => {
  useEffect(() => {
    targetRefMap[target]?.current?.scrollIntoView(options)
  }, [targetRefMap, target, options])
}

export const getAccountTabs = (isPulseEnabled: boolean, isPreferencesDisabled: boolean, isAlignPaydayEligible: boolean): AccountTabsType => {
  return {
    Profile: 'profile',
    ...(isPulseEnabled && { Pulse: 'pulse' }),
    ...{ PaymentMethods: isAlignPaydayEligible ? 'payment-settings' : 'payment-methods' },
    ...(!isPreferencesDisabled && { Preferences: 'preferences' }),
    Security: 'security',
  }
}

export const AccountTabs: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const isBpMediumUp: boolean = useIsBreakpointMediumAndUp() as boolean
  const tabContainerClassName: ClassValue = clsx(styles.tabContainer, isBpMediumUp && styles.bpMediumUp)
  const isPulseEnabled: boolean = useIsPulseEnabled()
  const isPreferencesDisabled = useFlag('consumer-portal-preferences-disabled', false)
  const isAlignPaydayEligible = useIsAlignPaydayEligible()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const AccountTabs = getAccountTabs(isPulseEnabled, isPreferencesDisabled, isAlignPaydayEligible)
  type AccountTab = (typeof AccountTabs)[keyof typeof AccountTabs]

  const isAccountTab = isValueOfObject(AccountTabs)

  const tabList = 'acc'
  const tabAccSearchParam = useSearchParam(`tab-${tabList}`)
  const [tabValue, setTabValue] = useState<AccountTab>(isAccountTab(tabAccSearchParam) ? tabAccSearchParam : AccountTabs.Profile)
  const tabRefs = useTabRefs(AccountTabs)
  const isTabValueEqualToTabSearchParam = tabValue === tabAccSearchParam
  useScrollIntoView(tabRefs, tabValue as string, { behavior: 'smooth', block: 'nearest', inline: 'start' })
  useTabDeepLink(tabValue as string, tabList)

  useEffect(() => {
    if (!isTabValueEqualToTabSearchParam) {
      setTabValue((tabAccSearchParam as AccountTab) ?? AccountTabs.Profile)
    }
  }, [])

  useEffect(() => {
    if (isAlignPaydayEligible && tabValue === 'payment-settings') {
      logEvent(TrackingEvent.ALIGN_PAY_DAY.FEATURE_DISPLAYED)
    }
  }, [isAlignPaydayEligible, tabValue])

  return (
    <div className={styles.container}>
      <div className={tabContainerClassName}>
        <Tabs value={tabValue as string} onChange={(value) => setTabValue(value as AccountTab)} animation={!isTabValueEqualToTabSearchParam}>
          {Object.values(AccountTabs).map((item: AccountTabType) => {
            return (
              <Tabs.Item key={item} value={item} ref={tabRefs[item]} testNameSpace={item}>
                {t(`account:tab:${item}:title`)}
              </Tabs.Item>
            )
          })}
        </Tabs>
      </div>
      <Divider margin='No' kind='Hairline' />
      <TabPanel selectedValue={tabValue as string} value={AccountTabs.Profile}>
        <ProfileTab />
      </TabPanel>
      {isPulseEnabled && (
        <TabPanel selectedValue={tabValue as string} value={AccountTabs.Pulse as string}>
          <PulseTab />
        </TabPanel>
      )}
      <TabPanel selectedValue={tabValue as string} value={AccountTabs.PaymentMethods}>
        {isAlignPaydayEligible && <PreferredDayRemoval />}
        <PaymentMethods />
      </TabPanel>
      {!isPreferencesDisabled && (
        <TabPanel selectedValue={tabValue as string} value={AccountTabs.Preferences as string}>
          <PreferencesTab />
        </TabPanel>
      )}
      <TabPanel selectedValue={tabValue as string} value={AccountTabs.Security}>
        <SecurityTab />
      </TabPanel>
    </div>
  )
}
